import { useCallback, useEffect, useMemo, useState } from 'react';

import { useFloorplanMapQuery } from 'data/queries/useFloorplanMapQuery.hook';
import { Floorplan, FloorplanPlacement } from 'floorplan/pages/native-floorplan/types';
import {
  collectSections,
  matchesSection,
  resolveSelectedSection,
} from 'floorplan/pages/native-floorplan/utils/sections';
import { CoworkerGroupResponse } from 'store/api-client/coworker-types';
import { useCoworkerGroups } from './use-coworker-groups.hook';
import { useFloorplanMapDate } from './use-floorplan-map-date.hook';
import { useFavoritesHighlight } from './use-favorites-highlight.hook';
import { useFocusedResource } from './use-focused-resource.hook';
import { useSelectedSectionUuid } from './use-selected-section.hook';

interface UseNativeFloorplanMapValues {
  /** The placement whose details sheet is open, if any. */
  selectedPlacement: FloorplanPlacement | null;
  closeDetails: VoidFunction;
  /** The day availability is shown for, `YYYY-MM-DD`. */
  date: string;
  placements: FloorplanPlacement[];
  /**
   * Placements the section filter excludes — and, while favorites are highlighted, every placement
   * that is not one. They stay on the plan, dimmed and unclickable — see `NATIVE_FLOORPLAN_DIMMED_OPACITY`.
   */
  dimmedResourceUuids: Set<string>;
  /** The favorites to ring while the highlight is on; null while it is off. */
  highlightedFavoriteUuids: ReadonlySet<string> | null;
  /** The user's favorites (highlight on or not), and whether the favorites feature is on at all. */
  favoriteResourceUuids: ReadonlySet<string>;
  isFavoritesEnabled: boolean;
  isFavoritesLoaded: boolean;
  isLoading: boolean;
  isError: boolean;
  selectedResourceUuid: string | null;
  /**
   * The desk a coworker jump asked for, if it is on this floor — the canvas centres, zooms and rings
   * it. Distinct from selection: no details open, and any tap clears it.
   */
  focusedResourceUuid: string | null;
  /**
   * Focus mode (§C3): the coworker group the url names, resolved against the requester's groups and
   * the teams gate. Null when there is none, teams are off, or the uuid is stale (deleted group).
   */
  activeGroup: CoworkerGroupResponse | null;
  /** `activeGroup`'s members as a set for the canvas; null without an active group. */
  focusGroupMemberUuids: ReadonlySet<string> | null;
  onPlacementClick: (resourceUuid: string) => void;
}

/**
 * Placements for the floor currently on screen.
 *
 * The floor itself is resolved a level up (`useResourcesView`), because the floor chips render with
 * the page's other controls while the map is full-bleed underneath them.
 *
 * The selected day is sent with the request. The endpoint still answers for the current server time
 * and ignores it, but it is wired through now — same as the admin builder does — so the date
 * navigator and filters plug in without reshaping the data flow.
 */
export const useNativeFloorplanMap = (floorplan: Floorplan | null): UseNativeFloorplanMapValues => {
  const [selectedResourceUuid, setSelectedResourceUuid] = useState<string | null>(null);

  const { date } = useFloorplanMapDate();
  const selectedSectionUuid = useSelectedSectionUuid();
  const { focusedResourceUuid: requestedFocusUuid, clearFocus } = useFocusedResource();
  const { placements, isLoading, isError } = useFloorplanMapQuery({ floorplanUuid: floorplan?.uuid, date });

  // Focus mode (§C3): the group the url names, as the whole page resolves it — see `useCoworkerGroups`.
  const { activeGroup, focusGroupMemberUuids } = useCoworkerGroups();

  const { isFavoritesEnabled, isFavoritesLoaded, favoriteResourceUuids, highlightedFavoriteUuids } =
    useFavoritesHighlight();

  const dimmedResourceUuids = useMemo(() => {
    // Resolved against this floor's sections — see `resolveSelectedSection`.
    const section = resolveSelectedSection(collectSections(placements), selectedSectionUuid);

    return new Set(
      placements
        .filter(
          placement =>
            !matchesSection(placement, section) ||
            (!!highlightedFavoriteUuids && !highlightedFavoriteUuids.has(placement.resource.uuid)),
        )
        .map(({ resource }) => resource.uuid),
    );
  }, [placements, selectedSectionUuid, highlightedFavoriteUuids]);

  // Toggles off when the selected placement is clicked again, so a click always has a visible effect.
  // Any tap also ends a coworker focus: the user is now pointing at something themselves (§C2).
  const onPlacementClick = useCallback(
    (resourceUuid: string) => {
      clearFocus();
      setSelectedResourceUuid(current => (current === resourceUuid ? null : resourceUuid));
    },
    [clearFocus],
  );

  // Only a desk actually on this floor can be focused; a stale or foreign uuid in the url is ignored.
  const focusedResourceUuid = useMemo(
    () =>
      requestedFocusUuid && placements.some(({ resource }) => resource.uuid === requestedFocusUuid)
        ? requestedFocusUuid
        : null,
    [requestedFocusUuid, placements],
  );

  const selectedPlacement = useMemo(
    () => placements.find(({ resource }) => resource.uuid === selectedResourceUuid) ?? null,
    [placements, selectedResourceUuid],
  );

  const closeDetails = useCallback(() => setSelectedResourceUuid(null), []);

  // A refetch can drop the selected desk — a different date, or someone else booking it. Clearing
  // the uuid too, not just hiding the sheet, stops it from reopening on its own if that desk comes
  // back in a later response.
  useEffect(() => {
    if (selectedResourceUuid && !isLoading && !selectedPlacement) {
      setSelectedResourceUuid(null);
    }
  }, [selectedResourceUuid, selectedPlacement, isLoading]);

  // Filtering the selected desk out has to close its details too: the map takes it out of reach, so
  // leaving the sheet open would keep offering a desk the user can no longer point at.
  useEffect(() => {
    if (selectedResourceUuid && dimmedResourceUuids.has(selectedResourceUuid)) {
      setSelectedResourceUuid(null);
    }
  }, [selectedResourceUuid, dimmedResourceUuids]);

  return {
    date,
    placements,
    dimmedResourceUuids,
    highlightedFavoriteUuids,
    favoriteResourceUuids,
    isFavoritesEnabled,
    isFavoritesLoaded,
    isLoading,
    isError,
    selectedResourceUuid,
    focusedResourceUuid,
    activeGroup,
    focusGroupMemberUuids,
    selectedPlacement,
    onPlacementClick,
    closeDetails,
  };
};
